import { ScrollManager, VirtualScrollConfig } from './core';
import { head, map, pipe, slice, sum, zipWithIndex } from '@fxts/core';
import { CssUnitParser } from '../../css-unit-parser.ts';

interface VirtualItem<D> {
  data: D;
  index: number;
  height: number;
  top: number;
}

export class VirtualScrollManager<D, C> extends ScrollManager<D, C> {
  private readonly cssUnitParser = new CssUnitParser();
  private virtualContainer: HTMLElement;
  private scrollableArea: HTMLElement;
  private virtualItems: Pick<VirtualItem<D>, 'data' | 'height'>[] = [];
  private totalHeight = 0;
  private startIndex = 0;
  private endIndex = 0;
  private isScrolling = false;
  private scrollTimeout?: number;
  private resizeObserver?: ResizeObserver;
  private containerHeightParser?: () => number;

  constructor(private enhancedConfig: VirtualScrollConfig<D, C>) {
    super(enhancedConfig as VirtualScrollConfig<D, C>);

    this.virtualContainer = this.createVirtualContainer();
    this.scrollableArea = this.createScrollableArea();
    this.setupResizeHandling();

    this.initialize();
  }

  protected get virtualConfig(): VirtualScrollConfig<D, C> {
    return this.enhancedConfig;
  }

  private setupResizeHandling(): void {
    if (!this.virtualConfig.responsive) return;

    const handleResize = () => {
      this.cssUnitParser.clearCache();
      this.recalculateAll();
    };

    window.addEventListener('resize', handleResize, { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize, { passive: true });
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(handleResize);
      this.resizeObserver.observe(this.container);
    }
  }

  private getContainerHeight(): number {
    if (!this.containerHeightParser) {
      this.containerHeightParser = this.cssUnitParser.dynamic(
        this.virtualConfig.containerHeight,
        this.container.parentElement || undefined,
      );
    }

    return this.containerHeightParser();
  }

  private getItemHeight(item: D): number {
    const { itemHeight } = this.virtualConfig;

    if (typeof itemHeight === 'function') {
      const heightValue = itemHeight(item);
      return this.cssUnitParser.toPixel(heightValue, this.container);
    }

    return this.cssUnitParser.toPixel(itemHeight, this.container);
  }

  private getGap(): number {
    if (!this.virtualConfig.gap) return 0;
    return this.cssUnitParser.toPixel(this.virtualConfig.gap, this.container);
  }

  protected initialize() {
    this.setupVirtualContainer();
    this.calculateVisibleRange();
    this.renderVisibleItems();
    this.updateScrollableHeight();

    const containerHeight = this.getContainerHeight();
    if (this.totalHeight < containerHeight) this.load();
  }

  protected render(data: D[]) {
    const newItems = data.map((d) => this.createVirtualItem(d));
    this.virtualItems.push(...newItems);

    this.calculateTotalHeight();
    this.calculateVisibleRange();
    this.renderVisibleItems();
    this.updateScrollableHeight();
  }

  private recalculateAll(): void {
    this.containerHeightParser = undefined;

    this.virtualItems = this.virtualItems.map((item) => ({
      ...item,
      height: this.getItemHeight(item.data),
    }));

    this.calculateTotalHeight();
    this.setupVirtualContainer();
    this.calculateVisibleRange();
    this.renderVisibleItems();
    this.updateScrollableHeight();
  }

  private setupVirtualContainer() {
    const containerHeight = this.getContainerHeight();
    this.container.style.height = `${containerHeight}px`;
    this.container.style.overflow = 'auto';
    this.container.style.position = 'relative';

    if (!this.virtualContainer) this.virtualContainer = this.createVirtualContainer();
    if (!this.scrollableArea) this.scrollableArea = this.createScrollableArea();

    if (!this.container.contains(this.scrollableArea)) {
      this.container.appendChild(this.scrollableArea);
    }

    if (!this.container.contains(this.virtualContainer)) {
      this.container.appendChild(this.virtualContainer);
    }
  }

  private createVirtualContainer(): HTMLElement {
    const virtualContainer = document.createElement('div');
    virtualContainer.style.position = 'relative';
    virtualContainer.style.width = '100%';
    if (this.virtualConfig.containerKlass) {
      virtualContainer.classList.add(this.virtualConfig.containerKlass);
    }
    return virtualContainer;
  }

  private createScrollableArea(): HTMLElement {
    const scrollableArea = document.createElement('div');
    scrollableArea.style.position = 'absolute';
    scrollableArea.style.top = '0';
    scrollableArea.style.left = '0';
    scrollableArea.style.width = '100%';
    scrollableArea.style.height = '1px';
    return scrollableArea;
  }

  private calculateTotalHeight() {
    const gap = this.getGap();
    this.totalHeight = pipe(
      this.virtualItems,
      zipWithIndex,
      map(([index, virtualItem]) => virtualItem.height + (index > 0 ? gap : 0)),
      sum,
    );
  }

  private calculateVisibleRange() {
    const scrollTop = this.container.scrollTop;
    const containerHeight = this.getContainerHeight();
    const overscan = this.virtualConfig.overscan || 5;
    const gap = this.getGap();

    let startIndex = 0;
    let endIndex = 0;
    let accumulatedHeight = 0;

    // 시작 인덱스 찾기
    this.virtualItems.some((virtualItem, index) => {
      const itemWithGap = virtualItem.height + (index > 0 ? gap : 0);
      const stop = accumulatedHeight + itemWithGap >= scrollTop;

      if (stop) {
        startIndex = Math.max(0, index - overscan);
      } else {
        accumulatedHeight += itemWithGap;
      }

      return stop;
    });

    // 끝 인덱스 찾기
    accumulatedHeight = 0;
    this.virtualItems.some((virtualItem, index) => {
      const itemWithGap = virtualItem.height + (index > 0 ? gap : 0);
      const stop = accumulatedHeight >= containerHeight + scrollTop;

      if (stop) {
        endIndex = Math.min(this.virtualItems.length - 1, index + overscan);
      } else {
        accumulatedHeight += itemWithGap;
      }

      return stop;
    });

    if (endIndex === 0) {
      endIndex = Math.min(this.virtualItems.length - 1, startIndex + overscan);
    }

    this.startIndex = startIndex;
    this.endIndex = endIndex;
  }

  private renderVisibleItems() {
    this.virtualContainer.innerHTML = '';
    const fragment = document.createDocumentFragment();
    const gap = this.getGap();

    let top = pipe(
      this.virtualItems,
      slice(0, this.startIndex),
      zipWithIndex,
      map(([index, virtualItem]) => {
        return virtualItem.height + (index > 0 ? gap : 0);
      }),
      sum,
    );

    for (let index = this.startIndex; index <= this.endIndex && index < this.virtualItems.length; index++) {
      const item = this.virtualItems[index];
      if (!item) continue;

      const { data, height } = item;
      const virtualItem: VirtualItem<D> = {
        data,
        index,
        height,
        top,
      };

      const element = this.createItemElement(virtualItem);
      fragment.appendChild(element);
      top += height + gap;
    }

    this.virtualContainer.appendChild(fragment);
  }

  private createItemElement(virtualItem: VirtualItem<D>): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.top = `${virtualItem.top}px`;
    wrapper.style.left = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = `${virtualItem.height}px`;

    if (this.virtualConfig.itemKlass) {
      wrapper.classList.add(this.virtualConfig.itemKlass);
    }

    if (this.virtualConfig.onRender) {
      const itemElements = this.virtualConfig.onRender([virtualItem.data]);
      const itemElement = pipe(itemElements, head);
      if (itemElement) wrapper.appendChild(itemElement);
    }

    return wrapper;
  }

  private createVirtualItem(data: D): (typeof this.virtualItems)[number] {
    return {
      data,
      height: this.getItemHeight(data),
    };
  }

  private updateScrollableHeight() {
    this.scrollableArea.style.height = `${this.totalHeight}px`;
  }

  private handleScroll = () => {
    if (this.isScrolling) return;

    this.isScrolling = true;

    requestAnimationFrame(async () => {
      this.calculateVisibleRange();
      this.renderVisibleItems();

      // 스크롤 끝 감지
      const scrollTop = this.container.scrollTop;
      const scrollHeight = this.container.scrollHeight;
      const clientHeight = this.container.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - (this.virtualConfig.threshold || 200)) {
        if (this.hasMore) await this.load();
      }

      this.isScrolling = false;
    });
  };

  public on(): this {
    if (this.isInitialized) this.reset();

    this.container.addEventListener('scroll', this.handleScroll, { passive: true });
    this.createScrollEvent();

    requestAnimationFrame(() => {
      this.container.scrollTop = this.scroll;
    });

    return this;
  }

  public off(): this {
    this.container.removeEventListener('scroll', this.handleScroll);
    return this;
  }

  public reset(): this {
    this.virtualItems = [];
    this.totalHeight = 0;
    this.startIndex = 0;
    this.endIndex = 0;

    this.container = this.createContainer();
    this.initialize();
    return this;
  }

  public destroy(): this {
    this.off();
    this.items.clear();
    this.virtualItems = [];
    this.resizeObserver?.disconnect();

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    return this;
  }

  public scrollToIndex(index: number): this {
    if (index < 0 || index >= this.virtualItems.length) return this;

    const gap = this.getGap();

    this.container.scrollTop = pipe(
      this.virtualItems,
      slice(0, index),
      zipWithIndex,
      map(([index, virtualItem]) => {
        const itemHeight = virtualItem.height || 0;
        const gapHeight = index > 0 ? gap : 0;
        return itemHeight + gapHeight;
      }),
      sum,
    );

    return this;
  }

  public recalculate(): this {
    this.recalculateAll();
    return this;
  }
}
